'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpFormData {
  fanName: string;
  email: string;
  password: string;
  confirmPassword: string;
  fanType: string;
  agreeTerms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const passwordValue = watch('password');

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    setAuthError('');
    try {
      await signUp(data.email, data.password, {
        fullName: data.fanName,
        fanName: data.fanName,
        fanType: data.fanType || '',
      });
      router.push('/home-dashboard');
    } catch (error: any) {
      setAuthError(error?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      {authError && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl fade-in" style={{ backgroundColor: '#fff5f5', border: '1px solid rgba(229,62,62,0.3)' }}>
          <span className="text-lg shrink-0">😭</span>
          <p className="text-sm font-semibold" style={{ color: '#c0392b' }}>{authError}</p>
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor="signup-name" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Fan name
        </label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          placeholder="What do they call you at the stadium?"
          suppressHydrationWarning
          className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all duration-150"
          style={{
            backgroundColor: '#ede8dc',
            border: `1.5px solid ${errors.fanName ? '#e53e3e' : '#d8ceb8'}`,
            color: '#0f0f0f',
          }}
          {...register('fanName', {
            required: 'Fan name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' },
          })}
        />
        {errors.fanName && (
          <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.fanName.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="signup-email" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Email address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@fanxietyclub.com"
          suppressHydrationWarning
          className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all duration-150"
          style={{
            backgroundColor: '#ede8dc',
            border: `1.5px solid ${errors.email ? '#e53e3e' : '#d8ceb8'}`,
            color: '#0f0f0f',
          }}
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="signup-fantype" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Fan type
          <span className="ml-1.5 text-xs font-normal" style={{ color: '#5a5040' }}>(optional)</span>
        </label>
        <select
          id="signup-fantype"
          className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all duration-150 appearance-none"
          style={{ backgroundColor: '#ede8dc', border: '1.5px solid #d8ceb8', color: '#0f0f0f' }}
          {...register('fanType')}
        >
          <option value="">Which fan are you?</option>
          <option value="optimist">😎 The Optimist — "This is our year!"</option>
          <option value="realist">😐 The Realist — Knows suffering is inevitable</option>
          <option value="conspiracy">👽 The Conspiracy Guy — Blames referees & aliens</option>
          <option value="loud">🔥 The Loud One — Screams constantly</option>
          <option value="ritual">🕯 The Ritual Master — Every lucky routine matters</option>
          <option value="quiet">💀 The Quiet One — Emotionally dead inside</option>
        </select>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="signup-password" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Password
        </label>
        <p className="text-xs" style={{ color: '#5a5040' }}>At least 8 characters. Make it stronger than your team's defense.</p>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Create a strong password"
            suppressHydrationWarning
            className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-medium outline-none transition-all duration-150"
            style={{
              backgroundColor: '#ede8dc',
              border: `1.5px solid ${errors.password ? '#e53e3e' : '#d8ceb8'}`,
              color: '#0f0f0f',
            }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-black/5 transition-colors duration-150" aria-label="Toggle password">
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" style={{ color: '#5a5040' }} />
          </button>
        </div>
        {errors.password && <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.password.message}</p>}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="signup-confirm" className="block text-sm font-bold" style={{ color: '#0f0f0f' }}>
          Confirm password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Repeat your password"
            suppressHydrationWarning
            className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-medium outline-none transition-all duration-150"
            style={{
              backgroundColor: '#ede8dc',
              border: `1.5px solid ${errors.confirmPassword ? '#e53e3e' : '#d8ceb8'}`,
              color: '#0f0f0f',
            }}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === passwordValue || 'Passwords do not match',
            })}
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-black/5 transition-colors duration-150" aria-label="Toggle confirm">
            <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" style={{ color: '#5a5040' }} />
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.confirmPassword.message}</p>}
      </div>

      <div className="space-y-1.5">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 mt-0.5 rounded shrink-0" suppressHydrationWarning {...register('agreeTerms', { required: 'You must agree to continue suffering' })} />
          <span className="text-sm leading-relaxed" style={{ color: '#5a5040' }}>
            I accept the{' '}
            <span className="font-bold cursor-pointer hover:opacity-80" style={{ color: '#0f0f0f' }}>Terms of Suffering</span>
            {' '}and{' '}
            <span className="font-bold cursor-pointer hover:opacity-80" style={{ color: '#0f0f0f' }}>Privacy Policy</span>
          </span>
        </label>
        {errors.agreeTerms && <p className="text-xs font-semibold" style={{ color: '#e53e3e' }}>{errors.agreeTerms.message}</p>}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3.5 rounded-xl text-sm font-black transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ backgroundColor: '#0f0f0f', color: '#f0e840' }}
      >
        {isLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
            <span>Joining the chaos…</span>
          </>
        ) : (
          '⚽ Join Fanxiety Club'
        )}
      </button>

      <p className="text-sm text-center" style={{ color: '#5a5040' }}>
        Already a member?{' '}
        <button type="button" onClick={onSwitchToLogin} className="font-bold transition-colors duration-150 hover:opacity-80" style={{ color: '#0f0f0f' }}>
          Sign in
        </button>
      </p>
    </form>
  );
}